import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button, ThemedText, ConfirmDialog } from '../components';
import labDocumentFormatService from '../api/services/labDocumentFormatService';
import { extractItems } from '../utils/responseUtils';
import { PageContainer } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel } from '../components/FormStyles';
import { StatusBadge } from '../components/Badge';
import { Plus, Edit2, Trash2, Microscope, Crosshair } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const StyledTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  min-height: 80px;
  resize: vertical;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const NumberInput = styled(StyledInput).attrs({ type: 'number' })`
  width: 120px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.muted};
  gap: ${props => props.theme.spacing.md};
`;

const DetectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.muted};
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SliderInput = styled.input.attrs({ type: 'range' })`
  flex: 1;
  accent-color: ${props => props.theme.colors.tint};
`;

const SliderValue = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  min-width: 36px;
  text-align: right;
`;

export const LabDocumentFormatsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    labVendor: '',
    formatCode: '',
    description: '',
    detectionPriority: 0,
    detectionConfidenceThreshold: 0.7,
    isDefault: false,
  });

  const fetchFormats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await labDocumentFormatService.getAll();
      setFormats(extractItems(response) || []);
    } catch {
      toast.error('Failed to load document formats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFormats(); }, [fetchFormats]);

  const openCreate = () => {
    setForm({
      name: '', labVendor: '', formatCode: '', description: '',
      detectionPriority: 0, detectionConfidenceThreshold: 0.7, isDefault: false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.formatCode.trim()) {
      toast.error('Format Code is required');
      return;
    }

    setSaving(true);
    try {
      await labDocumentFormatService.create(form);
      toast.success('Format created');
      setShowModal(false);
      fetchFormats();
    } catch {
      toast.error('Failed to create format');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await labDocumentFormatService.delete(deleteId);
      toast.success('Format deleted');
      setDeleteId(null);
      fetchFormats();
    } catch {
      toast.error('Failed to delete format');
    }
  };

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <ThemedText $size="h2" $weight="bold">Lab Document Formats</ThemedText>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button $variant="secondary" onClick={openCreate}>
              <Plus size={16} style={{ marginRight: 6 }} /> New Format
            </Button>
            <Button onClick={() => navigate('/lab-formats/calibrate')}>
              <Crosshair size={16} style={{ marginRight: 6 }} /> Train New Format
            </Button>
          </div>
        </PageHeader>

        {formats.length === 0 && !loading ? (
          <EmptyState theme={theme}>
            <Microscope size={48} />
            <ThemedText $size="lg">No document formats yet</ThemedText>
            <ThemedText $color="muted">Train a format to improve AI extraction accuracy for known lab document layouts</ThemedText>
          </EmptyState>
        ) : (
          <TableContainer theme={theme}>
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Name</Th>
                  <Th theme={theme}>Lab Vendor</Th>
                  <Th theme={theme}>Format Code</Th>
                  <Th theme={theme}>Priority</Th>
                  <Th theme={theme}>Detections</Th>
                  <Th theme={theme}>Status</Th>
                  <Th theme={theme}>Actions</Th>
                </tr>
              </Thead>
              <Tbody>
                {formats.map(f => (
                  <Tr key={f.id} theme={theme} onClick={() => navigate(`/lab-formats/${f.id}`)} style={{ cursor: 'pointer' }}>
                    <Td theme={theme}><strong>{f.name}</strong></Td>
                    <Td theme={theme}>{f.labVendor || '-'}</Td>
                    <Td theme={theme}><code>{f.formatCode}</code></Td>
                    <Td theme={theme}>{f.detectionPriority ?? 0}</Td>
                    <Td theme={theme}>
                      <DetectionCount theme={theme}>
                        {f.detectionCount ?? 0}
                        {f.lastDetectedAt && (
                          <span style={{ fontWeight: 400 }}>
                            &middot; {new Date(f.lastDetectedAt).toLocaleDateString()}
                          </span>
                        )}
                      </DetectionCount>
                    </Td>
                    <Td theme={theme}>
                      <StatusBadge isActive={f.isActive !== false} />
                    </Td>
                    <Td theme={theme} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button $variant="ghost" $size="small" onClick={() => navigate(`/lab-formats/calibrate/${f.id}`)}>
                          <Crosshair size={14} />
                        </Button>
                        <Button $variant="ghost" $size="small" onClick={() => navigate(`/lab-formats/${f.id}`)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button $variant="ghost" $size="small" onClick={() => setDeleteId(f.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Modal */}
        <ModalOverlay $isOpen={showModal} onClick={() => setShowModal(false)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <ModalHeader theme={theme}>
              New Format
            </ModalHeader>
            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Name *</FormLabel>
                <StyledInput theme={theme} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Element Materials Standard Report" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Lab Vendor</FormLabel>
                <StyledInput theme={theme} value={form.labVendor}
                  onChange={e => setForm(f => ({ ...f, labVendor: e.target.value }))}
                  placeholder="e.g. Element, ALS, SGS" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Format Code *</FormLabel>
                <StyledInput theme={theme} value={form.formatCode}
                  onChange={e => setForm(f => ({ ...f, formatCode: e.target.value }))}
                  placeholder="e.g. ELEMENT-STANDARD-V2" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Description</FormLabel>
                <StyledTextarea theme={theme} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the document format and layout" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Detection Priority</FormLabel>
                <NumberInput theme={theme} value={form.detectionPriority}
                  onChange={e => setForm(f => ({ ...f, detectionPriority: parseInt(e.target.value) || 0 }))}
                  min={0} />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Confidence Threshold</FormLabel>
                <SliderContainer theme={theme}>
                  <SliderInput theme={theme}
                    min={0} max={1} step={0.05}
                    value={form.detectionConfidenceThreshold}
                    onChange={e => setForm(f => ({ ...f, detectionConfidenceThreshold: parseFloat(e.target.value) }))} />
                  <SliderValue theme={theme}>{(form.detectionConfidenceThreshold * 100).toFixed(0)}%</SliderValue>
                </SliderContainer>
              </FormGroup>
              <FormGroup theme={theme} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.isDefault}
                  onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
                <FormLabel theme={theme} style={{ marginBottom: 0 }}>Set as default format</FormLabel>
              </FormGroup>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        <ConfirmDialog
          isOpen={!!deleteId}
          title="Delete Format"
          message="Are you sure you want to delete this document format? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      </PageContainer>
    </Layout>
  );
};
